import { PhysicianSchema } from '@/features/physician-schema/physician-schema';
import { useDistanceContext } from '@/hooks/use-distance-context';
import { useProviderLocation } from '@/hooks/use-provider-location';
import { cn } from '@/lib/utils';
import { NeuImage } from '@hcahealthcaredpx/neutron-react';

import { AddressSection, BodySection, DetailsSection } from './components';
import styles from './profile.module.css';
import { ProfileProps, ProviderLocation } from './profile.types';

export const OASPhysicianProfile = ({ fields, rendering }: ProfileProps): JSX.Element | null => {
  const physician = fields?.physicianData;
  const physicianFullName = `${physician.physicianFirstName || ''} ${physician.physicianMiddleInitial || ''} ${physician.physicianLastName || ''}`;
  const physicianNameAndDesignation = `${physicianFullName}, ${physician.physicianDesignation || ''}`;
  const { providerLocations } = useDistanceContext();

  const getProviderActiveLocation = useProviderLocation();
  const activeLocation = getProviderActiveLocation(
    physician.providerLocations,
    physician.physicianNpi
  ) as ProviderLocation;

  return (
    <>
      <PhysicianSchema {...fields?.physicianData} />
      <div data-test-id={rendering?.componentName}>
        <div className="container">
          <div className={cn('row d-flex align-items-center', styles.profileWrapper)}>
            <div className="col-lg-2 d-flex">
              <div className={styles.profileImageWrapper}>
                <NeuImage
                  alt={physicianFullName}
                  elevation={0}
                  gradient={0}
                  overlay={0}
                  src={physician.physicianPhotoUrl}
                  aria-hidden="true"
                />
              </div>
            </div>
            <div className={cn('col-lg-10', styles.profileWrapper)}>
              <div className="row">
                <DetailsSection
                  activeLocation={activeLocation}
                  gender={physician.gender}
                  languages={physician.Languages}
                  providerSpecialties={physician.providerSpecialties}
                  telehealthProvider={physician.telehealthProvider}
                />
                <AddressSection
                  activeLocation={activeLocation}
                  providerLocations={providerLocations || physician.providerLocations}
                  providerId={physician.physicianNpi}
                  website={physician.website}
                  mhdParticipant={physician.mhdParticipant}
                  onlineSchedulingUrl={physician?.onlineSchedulingUrl}
                  urlTitle={physician.urlTitle}
                />
              </div>
            </div>
          </div>
        </div>

        <BodySection
          featureVideos={physician.fadMediaList || []}
          pageTitle={physicianNameAndDesignation}
          physicianBio={physician.physicianBio}
          physicianCarePhilosophy={physician.physicianCarePhilosophy || ''}
          proceduresAndConditions={physician.proceduresAndConditions || []}
          providerSpecialties={physician.providerSpecialties || []}
          affiliations={physician.affiliations || []}
          website={physician.website}
          credentialsAndAccreditations={physician.credentialsAndAccreditations || []}
        />
      </div>
    </>
  );
};
